import React, { useEffect, useState, memo, useRef } from "react";
import { Line, Circle, Text, Label, Tag, Rect } from "react-konva";
import { setLocationData, updateModal } from "../../../store/actions/location";
import { useDispatch, useSelector } from "react-redux";
import { create } from "@mui/material/styles/createTransitions";

// Helper to get midpoints between vertices
function getMidpoints(points) {
  const midpoints = [];
  for (let i = 0; i < points.length; i += 2) {
    const x1 = points[i];
    const y1 = points[i + 1];
    const x2 = points[(i + 2) % points.length];
    const y2 = points[(i + 3) % points.length];
    midpoints.push([(x1 + x2) / 2, (y1 + y2) / 2]);
  }
  return midpoints;
}

const EditablePolygon = ({ stroke = "green", fill = "rgba(0,200,0,0.2)", floorKey, zone_id, enableCreateButton = false, createZone=false}) => {
  const [updatedPoints, setUpdatedPoints] = useState([]);
  const location_data = useSelector(state => state.location.location_data);
  const current_modal = useSelector(state => state.location?.current_modal);
  const [selected, setSelected] = useState(false);
  const [previewPoint, setPreviewPoint] = useState([]);
  const dispatch = useDispatch();
  const zone_coordinates_store = useSelector(state => state.location.location_data?.floors?.[floorKey]?.zones?.[zone_id]?.zone_coordinates);
  const zoneName = useSelector(state => state.location.location_data?.floors?.[floorKey]?.zones?.[zone_id]?.zone_name);
  const zone_coordinates_existing = zone_coordinates_store?.flatMap(coord => [coord.x, coord.y]);
  const [showTooltip, setShowTooltip] = useState(false);
  let zone_coordinates = zone_coordinates_existing;

  const points = zone_coordinates ? zone_coordinates : [];

  const updateZones = (updatedPoints) => {
    dispatch(setLocationData({
      ...location_data,
      floors: {
        ...location_data?.floors,
        [floorKey]: {
          ...location_data?.floors?.[floorKey],
          zones: {
            ...location_data?.floors?.[floorKey]?.zones,
            [zone_id]: {
              id: zone_id,
              ...location_data?.floors?.[floorKey]?.zones?.[zone_id],
              zone_coordinates: updatedPoints,
            }
          }
        }
      }
    }));
  }

  // Drag vertex
  const handleVertexDrag = (idx, e) => {
    const newPoints = points.slice();
    newPoints[idx * 2] = e.target.x();
    newPoints[idx * 2 + 1] = e.target.y();
    const updatedPoints = [];
    for (let i = 0; i < newPoints.length; i += 2) {
      updatedPoints.push({ x: newPoints[i], y: newPoints[i + 1] });
    }
    updateZones(updatedPoints);
    setPreviewPoint([]);
  };

    // Drag vertex
  const handleVertexDragMove = (idx, e) => {
    const newPoints = points.slice();
    newPoints[idx * 2] = e.target.x();
    newPoints[idx * 2 + 1] = e.target.y();
    setPreviewPoint(newPoints);
  };

  // Drag midpoint to add vertex
  const handleMidpointDragMove = (idx, e) => {
    const newPoints = points.slice();
    newPoints.splice((idx + 1) * 2, 0, e.target.x(), e.target.y());
    setPreviewPoint(newPoints);
  };

  // Drag midpoint to add vertex
  const handleMidpointDrag = (idx, e) => {
    const newPoints = points.slice();
    newPoints.splice((idx + 1) * 2, 0, e.target.x(), e.target.y());
    const updatedPoints = [];
    for (let i = 0; i < newPoints.length; i += 2) {
      updatedPoints.push({ x: newPoints[i], y: newPoints[i + 1] });
    }
    updateZones(updatedPoints);
    setPreviewPoint([]);
  };

  // Render vertices
  const vertexCircles = points.reduce((acc, val, idx, arr) => {
    if (idx % 2 === 0) {
      acc.push(
        <Circle
          key={`vertex-${idx}`}
          x={arr[idx]}
          y={arr[idx + 1]}
          radius={7}
          fill="#fff"
          draggable
          onDragEnd={e => handleVertexDrag(idx / 2, e)}
          onDragMove={e => handleVertexDragMove(idx / 2, e)}
          stroke="#0089EC"
          strokeWidth={2}
        />
      );
    }
    return acc;
  }, []);

  // Render midpoints
  const midpoints = getMidpoints(points);
  const midpointCircles = midpoints.map(([x, y], idx) => (
    <Circle
      key={`midpoint-${idx}`}
      x={x}
      y={y}
      radius={4}
      fill="#fff"
      draggable
      onDragEnd={e => handleMidpointDrag(idx, e)}
      onDragMove={e => handleMidpointDragMove(idx, e)}
      stroke="#0089EC"
      strokeWidth={2}
      opacity={0.8}
    />
  ));

  function getCentroid(points) {
    let x = 0, y = 0, n = points.length / 2;
    for (let i = 0; i < points.length; i += 2) {
      x += points[i];
      y += points[i + 1];
    }
    return { x: x / n, y: y / n };
  }

  function getBoundingBox(points) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (let i = 0; i < points.length; i += 2) {
      minX = Math.min(minX, points[i]);
      minY = Math.min(minY, points[i + 1]);
      maxX = Math.max(maxX, points[i]);
      maxY = Math.max(maxY, points[i + 1]);
    }
    return { width: maxX - minX, height: maxY - minY };
  }

  const centroid = getCentroid(points);
  const bbox = getBoundingBox(points);
  const fontSize = Math.min(14, Math.min(bbox.width, bbox.height) / 5);
  const textWidth = bbox.width * 0.9; // 90% of bbox width
  const textHeight = fontSize + 4;

  const textRef = useRef(null);
  const [isEllipsis, setIsEllipsis] = useState(false);

  // useEffect(() => {
  //   if (textRef.current) {
  //     // If the rendered text is wider than the allowed width, ellipsis is happening
  //     console.log('text width : ', textRef.current.getTextWidth(), textWidth);
  //     setIsEllipsis(textRef.current.getTextWidth() > textWidth);
  //   }
  // }, [zoneName, textWidth, fontSize]);

  useEffect(() => {
  if (textRef.current) {
    // Create a temporary Konva.Text to measure the full text width
    const tempText = new window.Konva.Text({
      text: zoneName || '',
      fontSize,
      fontFamily: "Verizon NHG eDS",
      fontStyle: "bold",
      fontWeight: 700,
      letterSpacing: 0.5,
      lineHeight: 18 / fontSize,
      padding: 0,
    });
    setIsEllipsis(tempText.getTextWidth() > textWidth);
    tempText.destroy();
  }
}, [zoneName, textWidth, fontSize]);


  return (
    <>
      {previewPoint.length > 0 &&
        <Line
          points={previewPoint}
          stroke={'#0089EC'}
          dash={[5, 5]}
          fill={'#0089EC1A'}
          strokeWidth={2}
          closed={true}
        />
      }
      <Text
        ref={textRef}
        x={centroid.x - textWidth / 2}
        y={centroid.y - textHeight / 2}
        width={textWidth}
        height={textHeight}
        text={zoneName || ''}
        fontSize={fontSize}
        fill="#006FC1"
        fontFamily="Verizon NHG eDS"
        fontStyle="bold"
        fontWeight={700}
        align="center"
        verticalAlign="middle"
        ellipsis={true}
      />
      <Line
        points={points}
        stroke={'#0089EC'}
        dash={[5, 5]}
        strokeWidth={2}
        fill={'#0089EC1A'}
        closed={true}
        onClick={e => {
          if(location_data?.addNewZoneButtonClicked==true) return;
          setSelected(true);
          if ((current_modal == 'NewFloor' || current_modal == 'AddFloor') && location_data?.action_type == 'edit') {
            dispatch(setLocationData({ ...location_data, editZoneSelected: true, newZones: { ...location_data?.newZones, [floorKey]: { zoneAlreadySelected: true, zone_id_selected: zone_id } }, selectedFloorKey: floorKey, renderZonePopupInFloorMapPopup: true  }));
          }
        }}
        onMouseEnter={() => isEllipsis && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      />
      { (isEllipsis && showTooltip) && (
        <Label
          x={centroid.x + 60}
          y={centroid.y - textHeight}
          opacity={0.95}
        >
          <Tag
            fill="#fff"
            stroke="#006FC1"
            cornerRadius={4}
            shadowBlur={4}
          />
          <Text
            text={zoneName}
            fontSize={fontSize}
            fill="#006FC1"
            fontFamily="Verizon NHG eDS"
            fontStyle="bold"
            fontWeight={700}
            padding={8}
            align="center"
          />
        </Label>
      )}
      {
        enableCreateButton && (<>
          {(location_data?.newZones?.[floorKey]?.zone_id_selected == zone_id  && vertexCircles)}
          {(location_data?.newZones?.[floorKey]?.zone_id_selected == zone_id  && midpointCircles)}
        </>)
      }
    </>
  );
};

export default memo(EditablePolygon);